/**
 * Opens Triyo.Web so the user can sign in with an existing web session path.
 * Mirrors legacy Login.js openWebPage (Office dialog when available).
 */
export function openTriyoWebLogin(appUrl: string): void {
  const url = appUrl.endsWith("/") ? appUrl : `${appUrl}/`;

  if (typeof Office !== "undefined" && Office.context?.ui?.displayDialogAsync) {
    Office.context.ui.displayDialogAsync(url, {
      height: 85,
      width: 60,
      displayInIframe: false,
    });
    return;
  }

  // Browser / non-Office preview fallback.
  window.open(url, "_blank", "noopener,noreferrer");
}

/**
 * Polls until `readToken` returns a non-empty value or `timeoutMs` elapses.
 * Legacy Login.js polls every 5s for the SSO cookie after the web dialog opens.
 */
export function waitForToken(
  readToken: () => string | null,
  options: { timeoutMs?: number; intervalMs?: number; signal?: AbortSignal } = {}
): Promise<string> {
  const timeoutMs = options.timeoutMs ?? 5 * 60 * 1000;
  const intervalMs = options.intervalMs ?? 2000;

  return new Promise((resolve, reject) => {
    const started = Date.now();

    const existing = readToken();
    if (existing) {
      resolve(existing);
      return;
    }

    const onAbort = () => {
      cleanup();
      reject(new Error("SSO sign-in was cancelled."));
    };

    const timer = window.setInterval(() => {
      if (options.signal?.aborted) {
        onAbort();
        return;
      }
      const token = readToken();
      if (token) {
        cleanup();
        resolve(token);
        return;
      }
      if (Date.now() - started >= timeoutMs) {
        cleanup();
        reject(new Error("Timed out waiting for a TRIYO web session. Sign in to the TRIYO web app, then try again."));
      }
    }, intervalMs);

    const cleanup = () => {
      window.clearInterval(timer);
      options.signal?.removeEventListener("abort", onAbort);
    };

    options.signal?.addEventListener("abort", onAbort);
  });
}
